import SharedDialog from '../../../../shared/shared-dialog';
import { useMutation } from '@tanstack/react-query';
import { transferCalendars } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { useState } from 'react';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface CalendarTransferDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  changeLogRoles: string[];
  isAllUsers: boolean;
}

export default function CalendarTransferDialog({
  open,
  handleClose,
  changeLogRoles,
  isAllUsers,
}: CalendarTransferDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const [transferFrom, setTransferFrom] = useState('');
  const [transferTo, setTransferTo] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      transferCalendars(
        {
          srcUser: transferFrom,
          destUser: transferTo,
          notifyByEmail: false,
        },
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'Calendar transfer has started.',
      });

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue transferring calendars. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        isAllUsers
          ? analytics.calendars.calendars.transfer_calendar
          : analytics.user_management.user_details.calendars.transfer_calendar,
        isAllUsers
          ? {
              module: ModuleList.modules.calendars.name,
              section: ModuleList.modules.calendars.sections.calendars,
            }
          : {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.calendars,
            }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.CanTransferCalendars.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Transfer Calendar Ownership"
      dialogContent={
        <Box>
          <Typography
            sx={{
              mt: 1,
            }}
          >
            Transfer all of a user's events to another user. Please note that
            only future, non-private events with at least one guest or resource
            will be transferred.
          </Typography>
          <Stack gap={2} sx={{ mt: 1 }}>
            <SharedAutocomplete
              autoFocus
              sources={['users']}
              autocompleteValue={transferFrom}
              setAutoCompleteValue={setTransferFrom}
              label="Transfer from:"
            />
            <SharedAutocomplete
              sources={['users']}
              autocompleteValue={transferTo}
              setAutoCompleteValue={setTransferTo}
              label="Transfer to:"
            />
          </Stack>

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Transfer Ownership',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'warning',
      }}
    />
  );
}
